import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { LuUnlink } from 'react-icons/lu';

import useLead from '~/features/leadDetails/hooks/useLead';

type Props = {
    loanRequestId: string;
    onUnlink: () => void;
    isUnlinking: boolean;
    canUnlink: boolean;
};

// Falls back to a short UUID prefix when `customId` is absent, which is the common case.
export const LinkedCaseCard = ({ canUnlink, isUnlinking, loanRequestId, onUnlink }: Props) => {
    const { t } = useTranslation();
    const { data: lead } = useLead(loanRequestId);

    if (!lead) return null;

    const shortId = lead.customId ?? lead.id.slice(0, 8).toUpperCase();

    return (
        <div
            className="flex items-start gap-3 rounded-md border border-outline-light bg-white p-3"
            data-testid="linked-case-card"
        >
            <span className="flex size-10 shrink-0 items-center justify-center rounded-md bg-surface-light text-sm font-bold text-midnight">
                {shortId.slice(0, 2)}
            </span>
            <div className="min-w-0 flex-1">
                <Link
                    to="/leads/details/$leadId"
                    params={{ leadId: lead.id }}
                    className="block truncate text-sm font-medium text-midnight hover:underline"
                >
                    {t('WHATSAPP_CHAT.USER_INFO.LINKED_CASES.CASE_LABEL', { shortId })}
                </Link>
                <p className="truncate text-sm text-grey">
                    {t(`STATUS.${lead.status}.STATUS`)}
                    {lead.type && (
                        <>
                            {' • '}
                            {t(`LEADS.TRAIT.TYPE.${lead.type}`)}
                        </>
                    )}
                </p>
            </div>
            {canUnlink && (
                <button
                    type="button"
                    onClick={onUnlink}
                    disabled={isUnlinking}
                    aria-label={t('WHATSAPP_CHAT.USER_INFO.LINKED_CASES.UNLINK')}
                    className="shrink-0 p-1 text-grey hover:text-alert-error disabled:opacity-50"
                >
                    <LuUnlink size={16} />
                </button>
            )}
        </div>
    );
};
